import { isPlatformBrowser, NgClass, NgTemplateOutlet } from "@angular/common";
import {
  Component,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation
} from "@angular/core";
import { AwsImage, CmsData } from "@dotcom-frontend/data";
import {
  BrandIconName,
  IconName,
  LgBrandIconComponent,
  LgIconComponent
} from "@legal-and-general/canopy";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { WysiwygDirective } from "../../../directives/wysiwyg.directive";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";
import { GlobalState } from "../../../store/global/global.reducer";
import { selectPageTitle } from "../../../store/global/global.selectors";
import { ImageComponent } from "../../globals/Image/Image.component";
import { LinkComponent } from "../../globals/Link/Link.component";
import { DynamicComponent } from "../../utils/dynamic-loader/dynamic.component";
import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import { SafeHtmlPipe } from "../../utils/pipes/safe-html..pipe";
import { DynamicComponentItem } from "../../utils/dynamic-loader/dynamic-component-item";

@Component({
  selector: "dotcom-frontend-box-block",
  templateUrl: "./BoxBlock.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrl: "./BoxBlock.component.scss",
  standalone: true,
  imports: [
    LgIconComponent,
    LgBrandIconComponent,
    ImageComponent,
    LoaderComponent,
    WysiwygDirective,
    SafeHtmlPipe,
    LinkComponent,
    NgClass,
    NgTemplateOutlet
  ]
})
export class BoxBlockComponent implements DynamicComponent, OnInit {
  @Input() data: CmsData | undefined;
  isDualBox = false;
  isDualBoxOverlay = false;
  isFlipCards = false;
  isLinkList = false;
  isQuickNav = false;
  isClickableBox = false;
  title: string | undefined;
  subtitle: string | undefined;
  body: string | undefined;
  bio: string | undefined;
  image: AwsImage | undefined;
  imageLocation = false;
  boxColour!: string;
  icon: IconName | undefined;
  brandIcon: BrandIconName | undefined;
  brandIconColour = "";
  brandIconAccentColour = "";
  brandIconAccentColours: any = {
    red: "--color-poppy-red",
    green: "--color-leafy-green",
    blue: "--color-super-blue",
    yellow: "--color-dandelion-yellow",
    charcoal: "--color-charcoal",
    "lightest-grey": "--color-white-smoke",
    "light-grey": "--color-platinum",
    "light-blue": "--color-super-blue-lightest",
    "light-green": "--color-leafy-green-lightest",
    "light-yellow": "--color-dandelion-yellow-lightest",
    "light-red": "--color-poppy-red-lightest"
  };
  darkBoxColours: string[] = ["green", "red", "charcoal", "blue"];
  ctaButtons: any;
  tag: string | undefined;
  pageTitle = "";
  isInColumnContentArea = false;
  isBrowser = isPlatformBrowser(this.platformId);
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  video: any;
  contentLayout: string | undefined;
  contentBgColor = "bg-color-";
  allowBackgroundFullWidthBleed = false;
  isParentBackgroundColourSet: boolean | undefined;
  brandIconFillColour!: string;
  hideOnMobileColumn = false;
  telephoneAvailabilityBlocks: any[] | undefined;
  brandIconAccentColourBack: any;
  brandIconColourBack: any;
  isCardToggled = false;
  authorName: string | undefined;
  authorSurname: string | undefined;
  authorTitle: string | undefined;
  profileImage: any;
  jobTitle: string | undefined;
  businessArea: string | undefined;
  department: string | undefined;
  jobInformation: string | undefined;
  videoBlock: DynamicComponentItem | undefined;
  isButtonAndLinkList: any;
  isLinkListAndButton: any;
  linkListButtons: DynamicComponentItem | undefined;
  lastButton: DynamicComponentItem | undefined;
  readTime: number | undefined;
  @Input() parent: string | undefined;
  navigationBoxBlock = false;

  constructor(
    private store: Store<{ global: GlobalState }>,
    private componentLoaderService: ComponentLoaderService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });
  }

  async ngOnInit() {
    if (this.data) {
      this.contentLayout = this.data["contentLayout"];
      this.isFlipCards = this.data["isFlipCards"];
      this.isDualBox = this.data["isDualBox"];
      this.isDualBoxOverlay = this.data["isDualBoxOverlay"];
      this.isInColumnContentArea = this.contentLayout === "column";
      this.isClickableBox =
        this.data["ctaStyle"] === "clickable_box" &&
        this.data["boxColour"] !== "transparent" &&
        !this.isDualBox &&
        !this.isDualBoxOverlay;

      if (this.isInColumnContentArea && this.data["hideOnMobileColumn"]) {
        this.hideOnMobileColumn = true;
      }

      if (this.data["contentType"] && this.data["contentType"][0] === "Page") {
        await this.setTeaserData();
      } else {
        this.setNormalData();
        this.setIcons();
        this.setImage();
        await this.setVideo();
        if (!this.isClickableBox) {
          await this.setCtas();
        }
        this.setTags();
        this.setColors();
      }

      if (this.data["contactNumberDetails"]) {
        this.telephoneAvailabilityBlocks = [];

        for (const [i, telephoneAvailabilityBlock] of this.data[
          "contactNumberDetails"
        ].entries()) {
          const telephoneAvailabilityComponent =
            await this.componentLoaderService.loadComponent(
              "TelephoneLineAvailabilityBlock",
              "blocks",
              {
                telephoneAvailabilityBlock,
                telephoneAvailabilityBlockId: i
              }
            );
          this.telephoneAvailabilityBlocks.push(telephoneAvailabilityComponent);
        }
      }
      if (this.data["parent"] === "navigation") {
        this.navigationBoxBlock = true;
      }
    }
  }

  async setTeaserData() {
    if (!this.data) return;

    this.title = this.data["teaserTitle"];
    this.body = this.data["teaserText"];
    this.authorTitle = this.data["authorTitle"];
    this.authorName = this.data["authorFirstName"];
    this.authorSurname = this.data["authorSurname"];
    this.jobTitle = this.data["jobTitle"];
    this.businessArea = this.data["businessArea"];
    this.department = this.data["department"];
    this.bio = this.data["teaserBio"];

    this.jobInformation = [this.jobTitle, this.businessArea, this.department].filter(Boolean).join(", ");

    if (this.data["buttonLabel"]) {
      this.ctaButtons = [
        await this.componentLoaderService.loadComponent("Button", "globals", {
          buttonStyle: "secondary",
          href: this.data["language"]["link"],
          text: this.data["buttonLabel"] + " " + this.data["authorFirstName"],
          blockId : this.data["contentLink"]["id"],
        })
      ];
    }

    if (this.data["authorProfileImage"] && this.data["authorProfileImage"]["url"]) {
      this.profileImage = this.data["authorProfileImage"];
    }

    if (this.data["teaserImage"] && this.data["teaserImage"]["url"]) {
      this.image = this.data["teaserImage"];
    }

    if (this.data["teaserIcon"]) {
      this.icon = this.data["teaserIcon"];
    }

    if (this.data["teaserButtonText"] && this.data["language"]["link"]) {
      this.ctaButtons = [
        await this.componentLoaderService.loadComponent("Button", "globals", {
          buttonStyle: this.data["teaserButtonStyle"],
          text: this.data["teaserButtonText"],
          href: this.data["language"]["link"],
          blockId : this.data["contentLink"]["id"],
        })
      ];
    }
  }

  setNormalData() {
    if (!this.data) return;

    this.title = this.data["title"];
    this.subtitle = this.data["subTitle"];
    this.body = this.data["body"];
    this.readTime = this.data["readTime"];
  }

  setImage() {
    if (!this.data) return;

    if (this.data["imageLocation"] === "bottom") {
      this.imageLocation = true;
    }

    if (this.data["image"] && this.data["image"]["url"]) {
      this.image = this.data["image"];
    }
  }

  async setVideo() {
    if (!this.data) return;

    if (this.data["video"]) {
      this.video = this.data["video"].expanded;
      this.video = {
        ...this.video,
        parent: "box"
      };

      if (this.video && !this.isFlipCards) {
        this.videoBlock = await this.componentLoaderService.loadComponent(
          "VideoBlock",
          "blocks",
          {
            ...this.video
          }
        );
      }
    }
  }

  setColors() {
    if (!this.data) return;

    if (this.data["boxColour"]) {
      this.boxColour = this.contentBgColor + this.data["boxColour"];
    }

    if (
      this.darkBoxColours.includes(this.data["boxColour"]) &&
      !this.isFlipCards
    ) {
      this.brandIconColour = "--color-white";
      this.brandIconAccentColour =
        this.brandIconAccentColours[this.data["boxColour"]];
    }

    this.brandIconAccentColourBack = "";
    this.brandIconColourBack = "--color-white";

    if (this.data["boxColour"] == "yellow") {
      this.brandIconAccentColour = this.brandIconAccentColours["yellow"];
      this.brandIconColourBack = "--color-charcoal";
    }

    if (this.isFlipCards) {
      this.brandIconAccentColour =
        this.brandIconAccentColours[this.data["boxColour"]];

      if (
        this.data["boxColour"] === "lightest-grey" ||
        this.data["boxColour"] === "transparent" ||
        this.data["boxColour"] === "white"
      ) {
        this.brandIconColourBack = "--color-charcoal";
      }
    }

    if (this.isDualBoxOverlay) {
      this.boxColour += "--transparent";
    }

    if (
      this.data["allowBackgroundFullWidthBleed"] &&
      !this.data["parentColour"]
    ) {
      this.allowBackgroundFullWidthBleed = true;
    }

    if (this.data["isParentBackgroundColourSet"]) {
      this.isParentBackgroundColourSet = true;
      this.allowBackgroundFullWidthBleed = false;

      if (this.data["parentColour"]) {
        this.boxColour = this.contentBgColor + this.data["parentColour"];
      }

      this.brandIconFillColour = this.data["parentColour"];
      this.brandIconAccentColour =
        this.brandIconAccentColours[this.brandIconFillColour];

      this.brandIconAccentColourBack =
        this.brandIconAccentColours[this.brandIconFillColour];
      this.brandIconColourBack = "--color-charcoal";

      if (this.darkBoxColours.includes(this.data["parentColour"])) {
        this.brandIconColour = "--color-white";
      } else {
        this.brandIconColour = "--color-charcoal";
      }
    }
  }

  setIcons() {
    if (!this.data) return;

    if (this.data["brandIconBlk"]) {
      this.brandIcon = this.data["brandIconBlk"]["brandIcon"];

      this.brandIconAccentColour =
        this.brandIconAccentColours[
          this.data["brandIconBlk"]["iconAccentColour"]
        ];
    }

    if (this.data["icon"]) {
      this.icon = this.data["icon"];
    }
  }

  toggleFlipCard() {
    this.isCardToggled = !this.isCardToggled;
  }

  setTags() {
    if (!this.data) return;

    if (this.data["category"] && this.data["category"].length > 0) {
      const tags = this.data["category"].map((element: any) => element.name);

      this.tag = tags.join(", ");
    }
  }

  async setCtas() {
    if (!this.data) return;

    this.isLinkList = this.data["ctaStyle"] === "link_list";

    this.isQuickNav = this.data["ctaStyle"] === "dropdown_selector";

    this.isButtonAndLinkList = this.data["ctaStyle"] === "button_and_link_list";

    this.isLinkListAndButton = this.data["ctaStyle"] === "link_list_and_button";

    if (this.isButtonAndLinkList) {
      const ctaButtonsCopy = [...this.data["ctaButtons"]];
      ctaButtonsCopy.shift();

      if (ctaButtonsCopy.length > 0) {
        this.linkListButtons = await this.componentLoaderService.loadComponent(
          "LinkList",
          "globals",
          {
            interactionContainer: "Box Block",
            ctaButtons: ctaButtonsCopy
          }
        );
      }
    } else if (this.isLinkListAndButton) {
      const ctaButtonsCopy = [...this.data["ctaButtons"]];
      const lastButtonData = ctaButtonsCopy.pop();
      lastButtonData["ctaStyle"] = "button";
      this.lastButton = await this.componentLoaderService.loadComponent(
        "Button",
        "globals",
        lastButtonData
      );

      if (ctaButtonsCopy.length > 0) {
        this.linkListButtons = await this.componentLoaderService.loadComponent(
          "LinkList",
          "globals",
          {
            interactionContainer: "Box Block",
            ctaButtons: ctaButtonsCopy
          }
        );
      }
    } else if (this.isInColumnContentArea && this.data["hideOnMobileColumn"]) {
      this.hideOnMobileColumn = true;
    }

    if (this.data["ctaButtons"]) {
      if (this.isLinkList) {
        this.ctaButtons = await this.componentLoaderService.loadComponent(
          "LinkList",
          "globals",
          {
            interactionContainer: "Box Block",
            ctaButtons: this.data["ctaButtons"]
          }
        );
      } else if (this.isQuickNav) {
        this.ctaButtons = await this.componentLoaderService.loadComponent(
          "QuickNav",
          "globals",
          {
            ctaButtons: this.data["ctaButtons"]
          }
        );
      } else {
        this.ctaButtons = [];
        for (const button of this.data["ctaButtons"].slice(0, 2)) {
          const buttonComponent =
            await this.componentLoaderService.loadComponent(
              "Button",
              "globals",
              {
                ...button,
                blockId : this.data["contentLink"]["id"],
                adobeData: {
                  interactionContainer: "Box Block",
                  interactionLink: button["href"],
                  interactionName: button["text"],
                  interactionPage: this.pageTitle,
                  interactionRegion: "",
                  interactionType: "ButtonClick",
                  interactionDetails: "",
                }
              }
            );
          this.ctaButtons.push(buttonComponent);
        }
      }
    }
  }
}
